<div class="booking-account-shell">
  <aside class="account-sidebar" [class.is-open]="sidebarOpen" aria-label="Account navigation">
    <div class="sidebar-logo">
      <a routerLink="/" [attr.aria-label]="operatorName + ' home'">
        <img [src]="logo" [alt]="operatorName">
      </a>
    </div>

    <div class="account-summary">
      <span class="account-avatar" aria-hidden="true">{{ userInitials }}</span>
      <div>
        <strong>{{ userName }}</strong>
        <span>{{ memberSince }}</span>
      </div>
    </div>

    <nav class="account-navigation">
      <a routerLink="/my-booking" class="is-active" (click)="closeSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="3" width="7" height="9" rx="1"/>
          <rect x="14" y="3" width="7" height="5" rx="1"/>
          <rect x="14" y="12" width="7" height="9" rx="1"/>
          <rect x="3" y="16" width="7" height="5" rx="1"/>
        </svg>
        My bookings
      </a>
      <a routerLink="/user-profile" (click)="closeSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
        My profile
      </a>
      <button type="button" (click)="bookBus()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/>
          <path d="m20 20-3.5-3.5"/>
        </svg>
        Book a bus
      </button>
      <span class="nav-divider"></span>
      <button type="button" (click)="logoutUser()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <path d="m16 17 5-5-5-5M21 12H9"/>
        </svg>
        Log out
      </button>
    </nav>
  </aside>

  @if (sidebarOpen) {
    <button
      type="button"
      class="sidebar-overlay"
      aria-label="Close account navigation"
      (click)="closeSidebar()">
    </button>
  }

  <main class="booking-account-main">
    <header class="account-topbar">
      <button
        type="button"
        class="sidebar-toggle"
        aria-label="Open account navigation"
        (click)="openSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18"/>
        </svg>
      </button>
      <h1>My bookings</h1>
    </header>

    <div class="booking-content">
      @if (!isLoggedIn) {
        <section class="booking-empty-state" aria-labelledby="signin-bookings-title">
          <span class="empty-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="7" width="15" height="9" rx="1.5"/>
              <path d="M18 10h2.5a1 1 0 0 1 .9.55L23 13v3h-4"/>
            </svg>
          </span>
          <h2 id="signin-bookings-title">Sign in to see your bookings</h2>
          <p>Access your upcoming trips, past journeys and electronic tickets.</p>
          <button
            type="button"
            class="primary-action"
            data-toggle="modal"
            data-target="#loginPopupForm">
            Sign in
          </button>
        </section>
      } @else {
        <div class="booking-filters" role="tablist" aria-label="Filter bookings by status">
          @for (filter of filters; track filter.key) {
            <button
              type="button"
              role="tab"
              [attr.aria-selected]="activeFilter === filter.key"
              [class.is-active]="activeFilter === filter.key"
              (click)="setFilter(filter.key)">
              {{ filter.label }}
            </button>
          }
        </div>

        @if (profileLoading || gettingTrips) {
          <div class="booking-list" aria-busy="true" aria-label="Loading bookings">
            @for (placeholder of [1, 2, 3]; track placeholder) {
              <article class="booking-row booking-skeleton">
                <span class="skeleton-icon"></span>
                <div class="skeleton-copy">
                  <span></span>
                  <span></span>
                </div>
                <span class="skeleton-status"></span>
                <span class="skeleton-price"></span>
              </article>
            }
          </div>
        } @else if (errorMessage) {
          <section class="booking-empty-state is-error" aria-live="polite">
            <span class="empty-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 7v6M12 17h.01"/>
              </svg>
            </span>
            <h2>Bookings unavailable</h2>
            <p>{{ errorMessage }}</p>
            <button type="button" class="primary-action" (click)="loadTrips()">Try again</button>
          </section>
        } @else if (filteredBookings.length) {
          <div class="booking-list">
            @for (booking of filteredBookings; track trackBooking($index, booking)) {
              <article class="booking-row">
                <span class="booking-bus-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="7" width="15" height="9" rx="1.5"/>
                    <path d="M18 10h2.5a1 1 0 0 1 .9.55L23 13v3h-4"/>
                    <circle cx="7" cy="18" r="1.4"/>
                    <circle cx="17" cy="18" r="1.4"/>
                  </svg>
                </span>

                <div class="booking-route">
                  <h2>{{ booking.route }}</h2>
                  <p>{{ booking.date }} · {{ booking.time }} · Ref {{ booking.reference }}</p>
                </div>

                <span class="booking-status" [ngClass]="statusClass(booking.status)">
                  {{ booking.status }}
                </span>

                <div class="booking-price">
                  <strong>Ksh {{ booking.amount | number:'1.0-0' }}</strong>
                  <button type="button" (click)="openTicket(booking)">View ticket</button>
                </div>
              </article>
            }
          </div>
        } @else {
          <section class="booking-empty-state">
            <span class="empty-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="7" width="15" height="9" rx="1.5"/>
                <path d="M18 10h2.5a1 1 0 0 1 .9.55L23 13v3h-4"/>
              </svg>
            </span>
            <h2>No {{ emptyFilterLabel }}bookings</h2>
            <p>You don't have any trips in this category yet.</p>
            <button type="button" class="primary-action" (click)="bookBus()">Book a bus</button>
          </section>
        }
      }
    </div>
  </main>

  @if (selectedBooking; as ticket) {
    <div
      class="ticket-modal-backdrop"
      role="presentation"
      (click)="closeTicket()">
      <section
        class="ticket-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-reference"
        (click)="stopPropagation($event)">
        <button
          type="button"
          class="ticket-close"
          aria-label="Close ticket"
          (click)="closeTicket()">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m18 6-12 12M6 6l12 12"/></svg>
        </button>

        <header class="ticket-heading">
          <span>Booking reference</span>
          <div>
            <h2 id="ticket-reference">{{ ticket.reference }}</h2>
            <span class="booking-status" [ngClass]="statusClass(ticket.status)">
              {{ ticket.status }}
            </span>
          </div>
        </header>

        <div class="ticket-body">
          <div class="ticket-route">
            <div>
              <strong>{{ ticket.time }}</strong>
              <span>{{ ticket.from }}</span>
            </div>
            <div class="ticket-route-line" aria-hidden="true">
              <span></span>
              <svg viewBox="0 0 24 24">
                <rect x="3" y="7" width="15" height="9" rx="1.5"/>
                <path d="M18 10h2.5a1 1 0 0 1 .9.55L23 13v3h-4"/>
                <circle cx="7" cy="18" r="1.4"/>
                <circle cx="17" cy="18" r="1.4"/>
              </svg>
              <span></span>
            </div>
            <div class="ticket-arrival">
              <strong>{{ ticket.arrivalTime }}</strong>
              <span>{{ ticket.to }}</span>
            </div>
          </div>

          <dl class="ticket-details">
            <div><dt>Travel date</dt><dd>{{ ticket.date }}</dd></div>
            <div><dt>Seat(s)</dt><dd>{{ ticket.seats }}</dd></div>
            <div><dt>Pickup point</dt><dd>{{ ticket.pickup }}</dd></div>
            <div><dt>Drop-off point</dt><dd>{{ ticket.dropoff }}</dd></div>
            <div><dt>Passenger</dt><dd>{{ ticket.passenger }}</dd></div>
            <div><dt>Amount paid</dt><dd class="amount-paid">Ksh {{ ticket.amount | number:'1.0-0' }}</dd></div>
          </dl>

          <div class="ticket-qr-note">
            <span class="qr-placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <path d="M14 14h3v3h-3zM18 18h3v3h-3zM14 21h3M21 14v3"/>
              </svg>
            </span>
            <div>
              <strong>Show this booking to the conductor</strong>
              <span>Use the electronic ticket sent after payment confirmation.</span>
            </div>
          </div>

          <button type="button" class="download-ticket" (click)="downloadTicket()">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v12m0 0-4-4m4 4 4-4M4 21h16"/>
            </svg>
            Download ticket
          </button>
        </div>
      </section>
    </div>
  }
</div>
